import { type ReactNode, useLayoutEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import DisclosureRightSmallIcon from '../../icons/react/icon-disclosure-right-small';
import { HorizontalStack } from '../alignment/horizontal-stack';
import type { LinkElementType } from '../link';
import { TextLink } from '../link/text-link/text-link';
import { Tooltip } from '../tooltip/tooltip';
import { Typography, type TypographyProps } from '../typography';
import styles from './breadcrumbs.module.scss';

const OVERFLOW_BUFFER = 4; // pixels to account for minor discrepancies in overflow detection

const currentPageProps = {
  role: 'link',
  'aria-disabled': true,
  'aria-current': 'page',
} as const;

export interface Breadcrumb {
  className?: string;
  id: string;
  prefix?: ReactNode;
  inactiveLabel?: ReactNode;
  label: ReactNode;
  onClick?: VoidFunction;
  suffix?: ReactNode;
  to?: string;
  LinkElement?: LinkElementType;
}

interface BreadcrumbProps extends Breadcrumb {
  isLastCrumb: boolean;
  typography: TypographyProps;
}

export function BreadcrumbComponent({
  className,
  inactiveLabel,
  isLastCrumb,
  label,
  LinkElement,
  onClick,
  prefix,
  suffix,
  to,
  typography,
}: BreadcrumbProps) {
  const [labelRef, setLabelRef] = useState<HTMLSpanElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = labelRef;

    function handleResize() {
      setIsOverflowing(Boolean(element?.clientWidth && element?.scrollWidth && element.scrollWidth > element.clientWidth + OVERFLOW_BUFFER));
    }

    const resizeObserver = new ResizeObserver(handleResize);

    if (element) {
      resizeObserver.observe(element);
    }

    // Manually trigger for initial state
    handleResize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [labelRef]);

  const labelToShow = (isLastCrumb ? label : inactiveLabel) || label;

  const baseLabelProps = useMemo(
    () => ({
      ...typography,
      ...(isLastCrumb && to ? currentPageProps : {}),
    }),
    [isLastCrumb, to, typography],
  );

  const labelElement =
    isLastCrumb || !to ? (
      <Typography {...baseLabelProps} ref={setLabelRef}>
        {labelToShow}
      </Typography>
    ) : (
      <TextLink LinkElement={LinkElement} onClick={onClick} size="small" to={to} typography={typography} underline="hover">
        <Typography {...baseLabelProps} ref={setLabelRef}>
          {labelToShow}
        </Typography>
      </TextLink>
    );

  return (
    <>
      <Typography className={clsx(styles.breadcrumb, className)} {...typography}>
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          {prefix}

          {isOverflowing ? <Tooltip activator={labelElement}>{label}</Tooltip> : labelElement}

          {isLastCrumb ? suffix : null}
        </HorizontalStack>
      </Typography>
      {isLastCrumb ? null : <DisclosureRightSmallIcon aria-hidden className={styles.separator} width={16} />}
    </>
  );
}
